import React, { useMemo, useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import { Row, Col, Badge, Button, Form } from "react-bootstrap";
import ClickBack from "../../components/buttons/ClickBack";
import DynamicTable from "../../components/DynamicTable";
import { fetchConsultationsData, exportConsultations } from "./dataService";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import SearchBar from "../../components/SearchBar";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("fr", fr);

function ListScreen(props) {
  const [consultationsData, setConsultationsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [nbr, setNbr] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [dateDebut, setDateDebut] = useState(null);
  const [dateFin, setDateFin] = useState(null);
  const [typeAction, setTypeAction] = useState("");

  // Function to fetch consultations data
  const fetchData = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (dateDebut) filters.date_debut = format(dateDebut, "yyyy-MM-dd");
      if (dateFin) filters.date_fin = format(dateFin, "yyyy-MM-dd");
      if (typeAction) filters.action = typeAction;

      const data = await fetchConsultationsData(nbr, searchTerm, filters);
      setConsultationsData(data.data);
    } catch (error) {
      console.error("Error fetching consultations data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update search term and fetch data
  const handleSearch = (term) => {
    setSearchTerm(term);
    setNbr(1); // Reset to first page
  };

  // Handle export
  const handleExport = async () => {
    try {
      setExporting(true);
      const filters = { search: searchTerm };
      if (dateDebut) filters.date_debut = format(dateDebut, "yyyy-MM-dd");
      if (dateFin) filters.date_fin = format(dateFin, "yyyy-MM-dd");
      if (typeAction) filters.action = typeAction;

      await exportConsultations(filters);
    } catch (error) {
      console.error("Error exporting consultations:", error);
      alert("Erreur lors de l'export");
    } finally {
      setExporting(false);
    }
  };

  // useEffect to automatically load data when the component mounts
  useEffect(() => {
    fetchData();
  }, [nbr, searchTerm, dateDebut, dateFin, typeAction]);

  // Memoized columns for the DynamicTable component
  const columns = useMemo(() => {
    return [
      { Header: "ID", accessor: "id" },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ value }) => {
          const actionLabels = {
            'consultation_facture': 'Consultation Facture',
            'consultation_donnees_utilisateur': 'Consultation Utilisateur',
            'consultation_commande': 'Consultation Commande',
            'consultation_liste_utilisateurs': 'Liste Utilisateurs',
            'consultation_liste_commandes': 'Liste Commandes',
            'export_massif_donnees': 'Export Massif',
          };
          const actionColors = {
            'consultation_facture': 'bg-warning',
            'consultation_donnees_utilisateur': 'bg-info',
            'consultation_commande': 'bg-primary',
            'consultation_liste_utilisateurs': 'bg-secondary',
            'consultation_liste_commandes': 'bg-secondary',
            'export_massif_donnees': 'bg-danger',
          };
          return (
            <Badge className={actionColors[value] || 'bg-dark'}>
              {actionLabels[value] || value}
            </Badge>
          );
        }
      },
      {
        Header: "Utilisateur",
        accessor: "user",
        Cell: ({ row }) => {
          const user = row.original.user;
          return user ? `${user.prenom || ''} ${user.nom || ''} (${user.email || ''})` : 'N/A';
        }
      },
      {
        Header: "Type Ressource",
        accessor: "resource_type",
      },
      {
        Header: "ID Ressource",
        accessor: "resource_id",
        Cell: ({ value }) => value || 'N/A',
      },
      {
        Header: "Utilisateur Consulté",
        accessor: "consulted_user",
        Cell: ({ row }) => {
          const consultedUser = row.original.consulted_user;
          return consultedUser ? `${consultedUser.prenom || ''} ${consultedUser.nom || ''}` : 'N/A';
        }
      },
      {
        Header: "IP",
        accessor: "ip_address",
      },
      {
        Header: "Date",
        accessor: "created_at",
        Cell: ({ value }) =>
          format(new Date(value), "dd-MM-yyyy HH:mm:ss", { locale: fr }),
      },
    ].filter(Boolean);
  }, [nbr]);

  // Render loading message while data is being fetched
  if (!consultationsData) {
    return (
      <Container>
        <p>Chargement...</p>
      </Container>
    );
  }

  // Render the component
  return (
    <Container>
      <div className="consultationsList">
        {/* Breadcrumb navigation */}
        <p className="h6">Accueil / Administration / Consultations RGPD</p>

        {/* Header and filters */}
        <Row className="pt-4 pb-2">
          <Col style={{ display: "flex", alignItems: "center" }}>
            <ClickBack />
            <h1 className="p-1">Consultations RGPD (Lectures)</h1>
          </Col>
          <Col className="text-end">
            <Button
              variant="success"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? 'Export en cours...' : 'Exporter CSV'}
            </Button>
          </Col>
        </Row>
        <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />

        {/* Filtres de date et type d'action */}
        <Row className="mb-3 align-items-end">
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Date de début</Form.Label>
              <DatePicker
                selected={dateDebut}
                onChange={(date) => {
                  setDateDebut(date);
                  setNbr(1);
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date de début"
                isClearable
                className="form-control"
                locale="fr"
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Date de fin</Form.Label>
              <DatePicker
                selected={dateFin}
                onChange={(date) => {
                  setDateFin(date);
                  setNbr(1);
                }}
                dateFormat="dd/MM/yyyy"
                placeholderText="Date de fin"
                isClearable
                className="form-control"
                locale="fr"
              />
            </Form.Group>
          </Col>
          <Col xs="auto">
            <Form.Group>
              <Form.Label>Type d'action</Form.Label>
              <Form.Select
                value={typeAction}
                onChange={(e) => {
                  setTypeAction(e.target.value);
                  setNbr(1);
                }}
              >
                <option value="">Toutes les actions</option>
                <option value="consultation_facture">Consultation Facture</option>
                <option value="consultation_donnees_utilisateur">Consultation Utilisateur</option>
                <option value="consultation_commande">Consultation Commande</option>
                <option value="consultation_liste_utilisateurs">Liste Utilisateurs</option>
                <option value="consultation_liste_commandes">Liste Commandes</option>
                <option value="export_massif_donnees">Export Massif</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>

        {/* Render the DynamicTable component */}
        {loading ? (
          <p className="lead">Chargement...</p>
        ) : (
          <DynamicTable
            columns={columns}
            data={consultationsData?.data}
            link={consultationsData?.links}
            last_page={consultationsData?.last_page}
            setNbr={setNbr}
            nbr={nbr}
            total={consultationsData?.total}
            name="consultation"
          />
        )}
      </div>
    </Container>
  );
}

export default ListScreen;
